import { UserAddOutlined } from "@ant-design/icons";
import {
  Api,
  canDeleteContent,
  canManageContentActivation,
  hasPermission,
  ICharacter,
  PERMISSIONS,
} from "@model";
import { SearchBar, ISearchQuery } from "@components";
import { useIsCompact } from "../../hooks/useIsCompact";
import { Avatar, Button, Checkbox, Empty, Pagination, Popconfirm, Select, Spin, Table } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/lib/table";
import { useSearchParams } from "react-router-dom";

type CharacterListItem = ICharacter & {
  messageCount?: number;
  isDeleted?: boolean;
};

const CharacterListPage = () => {
  const isCompact = useIsCompact();
  const [searchParams, setSearchParams] = useSearchParams();
  const [characters, setCharacters] = useState<CharacterListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });

  const syncStateFromUrl = useCallback(() => {
    const q = searchParams.get("q") ?? "";
    setSearchQuery((prev) => (prev.q !== q ? { q } : prev));
    setCurrentPage(Math.max(1, Number(searchParams.get("page")) || 1));
    const ps = Number(searchParams.get("pageSize"));
    if (ps) setPageSize(Math.min(100, Math.max(10, ps)));
    const sf = searchParams.get("sortField");
    setSortField(sf || undefined);
    const so = searchParams.get("sortOrder");
    setSortOrder(so || undefined);
  }, [searchParams]);

  useEffect(() => {
    syncStateFromUrl();
  }, [syncStateFromUrl]);

  useEffect(() => {
    setLoading(true);
    Api.get(`/api/admin/panel/characters`, {
      params: {
        name: searchQuery.q,
        sortField,
        sortOrder,
        page: currentPage,
        limit: pageSize,
      },
    })
      .then((response) => {
        const data = response.data.data as CharacterListItem[];
        setCharacters(data);
        setLoading(false);
        const currentDataCount = data.length;
        if (currentDataCount < pageSize) {
          setTotalPages(currentPage);
        } else {
          setTotalPages(currentPage + 1);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setLoading(false);
      });
  }, [searchQuery.q, sortField, sortOrder, currentPage, pageSize]);

  const updateUrl = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
    updateUrl({
      page: String(page),
      ...(newPageSize ? { pageSize: String(newPageSize) } : {}),
    });
  };

  const navigate = useNavigate();
  const listReturnTo = searchParams.toString()
    ? `/panel/characters?${searchParams.toString()}`
    : "/panel/characters";
  const canEdit = hasPermission(PERMISSIONS.charactersEdit);
  const canCreate = hasPermission(PERMISSIONS.charactersCreate);
  const canActivate = canManageContentActivation();

  const updateActivation = useCallback(async (record: ICharacter, isActive: boolean) => {
    await Api.patch(`/api/admin/panel/characters/${record._id}/activation`, {
      isActive,
    });
    setCharacters((current) =>
      current.map((item) =>
        item._id === record._id ? { ...item, isActive } : item,
      ),
    );
  }, []);

  const deleteCharacter = useCallback(async (record: ICharacter) => {
    await Api.delete(`/api/admin/panel/characters/${record._id}`);
    setCharacters((current) => current.filter((item) => item._id !== record._id));
  }, []);

  const columns = useMemo(
    (): ColumnsType<ICharacter> => [
      {
        title: "Active",
        width: 50,
        dataIndex: "isActive",
        key: "isActive",
        render: (w: boolean, record) => (
          <Checkbox
            checked={w === true}
            disabled={!canActivate}
            onChange={(event) =>
              void updateActivation(record, event.target.checked)
            }
          />
        ),
      },
      {
        title: "Premium",
        width: 50,
        dataIndex: "isPremium",
        key: "isPremium",
        render: (w: boolean) => <Checkbox checked={w === true} disabled />,
      },
      {
        title: "IR",
        width: 50,
        dataIndex: "isAvailableInIran",
        key: "isAvailableInIran",
        render: (w: boolean) => <Checkbox checked={w === true} />,
      },
      {
        title: "#",
        width: 50,
        dataIndex: "_id",
        key: "_id",
        render: (w: string) => (
          <Avatar src={`/images/characters/${w}_sm.jpg`} />
        ),
      },
      {
        title: "Default name",
        width: 180,
        dataIndex: "name",
        key: "name",
        render: (name: string, record: ICharacter) =>
          canEdit ? (
          <a
            href={`/panel/characters/create/${record._id ?? ""}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/panel/characters/create/${record._id ?? ""}`, {
                state: { ...record, returnTo: listReturnTo },
              });
            }}
            style={{
              fontWeight: 600,
              color: "#1890ff",
              textDecoration: "underline",
            }}
          >
            {name}
          </a>
          ) : (
            name
          ),
      },
      {
        title: "messageCount",
        width: 120,
        dataIndex: "messageCount",
        key: "messageCount",
        sorter: true,
        sortOrder:
          sortField === "messageCount"
            ? sortOrder === "asc"
              ? "ascend"
              : "descend"
            : undefined,
      },
      {
        title: "Messages",
        width: 150,
        key: "messages",
        render: (_: unknown, record: ICharacter) =>
          canEdit ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/panel/characters/create/${record._id}?tab=messages`, {
                state: { ...record, returnTo: listReturnTo },
              });
            }}
            style={{
              background: "none",
              border: "none",
              color: "#1890ff",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            View Messages
          </button>
          ) : null,
      },
      {
        title: "Creator",
        width: 140,
        key: "creator",
        render: (_: unknown, record: ICharacter) => record.creator?.name ?? "-",
      },
      {
        title: "Actions",
        key: "actions",
        render: (_: unknown, record: ICharacter) =>
          canDeleteContent(
            record.creator,
            PERMISSIONS.charactersDelete,
            PERMISSIONS.charactersDeleteOwn,
          ) ? (
            <Popconfirm
              title="Delete this character?"
              onConfirm={() => void deleteCharacter(record)}
            >
              <Button danger size="small">Delete</Button>
            </Popconfirm>
          ) : null,
      },
    ],
    [
      navigate,
      sortField,
      sortOrder,
      listReturnTo,
      canActivate,
      canEdit,
      deleteCharacter,
      updateActivation,
    ],
  );

  const openCreate = () => {
    navigate(`/panel/characters/create`, { state: { returnTo: listReturnTo } });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isCompact ? "stretch" : "center",
          flexDirection: isCompact ? "column" : "row",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchBar
            style={{ width: "100%", margin: 0 }}
            value={searchQuery.q}
            onChange={(e) => {
              setSearchQuery(e);
              setCurrentPage(1);
              setLoading(true);
              updateUrl({ q: e.q || undefined, page: "1" });
            }}
            searchType="Enter"
          />
        </div>
        {(isCompact || canCreate) && (
          <div style={{ display: "flex", gap: 8 }}>
            {isCompact && (
              <Select
                allowClear
                placeholder="Sort by messages"
                value={sortField === "messageCount" ? sortOrder : undefined}
                onChange={(value) => {
                  if (value === "asc" || value === "desc") {
                    setSortField("messageCount");
                    setSortOrder(value);
                    updateUrl({ sortField: "messageCount", sortOrder: value });
                  } else {
                    setSortField(undefined);
                    setSortOrder(undefined);
                    updateUrl({ sortField: undefined, sortOrder: undefined });
                  }
                }}
                options={[
                  { value: "desc", label: "Most messages" },
                  { value: "asc", label: "Fewest messages" },
                ]}
                style={{ flex: 1, minWidth: 150 }}
              />
            )}
            {canCreate && (
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={openCreate}
                style={isCompact ? { flex: 1 } : undefined}
              >
                New
              </Button>
            )}
          </div>
        )}
      </div>
      {isCompact ? (
        <>
          <Spin spinning={loading}>
            {characters.length === 0 && !loading ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No characters" />
            ) : (
              characters.map((record) => {
                const deleted = record.isDeleted === true;
                const canDelete = canDeleteContent(
                  record.creator,
                  PERMISSIONS.charactersDelete,
                  PERMISSIONS.charactersDeleteOwn,
                );
                return (
                  <div
                    key={record._id}
                    style={{
                      display: "flex",
                      gap: 12,
                      padding: 12,
                      marginBottom: 8,
                      background: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      color: deleted ? "#de0000" : undefined,
                    }}
                  >
                    <Avatar
                      size={56}
                      src={`/images/characters/${record._id}_sm.jpg`}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          {canEdit ? (
                            <a
                              href={`/panel/characters/create/${record._id ?? ""}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(
                                  `/panel/characters/create/${record._id ?? ""}`,
                                  { state: { ...record, returnTo: listReturnTo } },
                                );
                              }}
                              style={{
                                fontWeight: 600,
                                color: deleted ? "#de0000" : "#1890ff",
                                wordBreak: "break-word",
                              }}
                            >
                              {record.name}
                            </a>
                          ) : (
                            <span style={{ fontWeight: 600, wordBreak: "break-word" }}>
                              {record.name}
                            </span>
                          )}
                          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                            {record.creator?.name ?? "No creator"}
                          </div>
                        </div>
                        {canDelete && (
                          <Popconfirm
                            title="Delete this character?"
                            onConfirm={() => void deleteCharacter(record)}
                          >
                            <Button danger size="small">
                              Delete
                            </Button>
                          </Popconfirm>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px 14px",
                          marginTop: 8,
                          alignItems: "center",
                          fontSize: 13,
                        }}
                      >
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox
                            checked={record.isActive === true}
                            disabled={!canActivate}
                            onChange={(event) =>
                              void updateActivation(record, event.target.checked)
                            }
                          />
                          Active
                        </label>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox checked={record.isPremium === true} disabled />
                          Premium
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Checkbox checked={record.isAvailableInIran === true} disabled />
                          IR
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 4,
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {record.messageCount ?? 0} messages
                        </span>
                        {canEdit && (
                          <Button
                            type="link"
                            size="small"
                            style={{ paddingRight: 0, color: "#1890ff" }}
                            onClick={() =>
                              navigate(
                                `/panel/characters/create/${record._id}?tab=messages`,
                                { state: { ...record, returnTo: listReturnTo } },
                              )
                            }
                          >
                            View messages
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </Spin>
          {(totalPages > 1 || currentPage > 1) && (
            <Pagination
              style={{ marginTop: 8, textAlign: "center" }}
              size="small"
              simple
              current={currentPage}
              pageSize={pageSize}
              total={totalPages * pageSize}
              onChange={handlePaginationChange}
            />
          )}
        </>
      ) : (
        <Table
          loading={loading}
          size="small"
          scroll={{ x: 960 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalPages * pageSize,
            onChange: handlePaginationChange,
          }}
          rowKey={(record) => record._id ?? ""}
          rowClassName={(us) =>
            (us as CharacterListItem).isDeleted === true ? "deleted-record" : ""
          }
          columns={columns}
          dataSource={characters}
          onChange={(_pagination, _filters, sorter) => {
            const urlUpdates: Record<string, string | undefined> = {};
            if (!Array.isArray(sorter) && sorter?.field != null) {
              const sf = String(sorter.field);
              const so = sorter.order === "ascend" ? "asc" : "desc";
              setSortField(sf);
              setSortOrder(so);
              urlUpdates.sortField = sf;
              urlUpdates.sortOrder = so;
            }
            updateUrl(urlUpdates);
          }}
        />
      )}
    </div>
  );
};

export default CharacterListPage;
